import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ENVIRONMENT, LoadMetaDataResponse } from '@ld.consumer-portal/models';
import { firstValueFrom } from 'rxjs';

export type { LoadMetaDataResponse, MetaData } from '@ld.consumer-portal/models';
import { LoggingService } from '@ld.consumer-portal/utils';

@Injectable({
  providedIn: 'root',
})
export class MetadataService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly loggingService = inject(LoggingService);

  private get cpApiUrl(): string {
    return this.env.appSettings.cpApiUrl || '';
  }

  private get apiNamespace(): string {
    return this.env.appSettings.apiNamespace || 'api/v1';
  }

  async loadMetadata(loanId: string, borrowerId: string): Promise<LoadMetaDataResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/borrowers/${borrowerId}/metadata`;

    try {
      return await firstValueFrom(this.http.get<LoadMetaDataResponse>(url));
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error fetching metadata for loan ${loanId}, borrower ${borrowerId}`);
      throw new Error(`Failed to fetch borrower metadata: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async shouldShowMessages(loanId: string, borrowerId: string): Promise<boolean> {
    try {
      const metadata = await this.loadMetadata(loanId, borrowerId);
      return metadata.showMessages;
    } catch {
      return false;
    }
  }

  async shouldShowContacts(loanId: string, borrowerId: string): Promise<boolean> {
    try {
      const metadata = await this.loadMetadata(loanId, borrowerId);
      return metadata.showContacts;
    } catch {
      return false;
    }
  }

  async shouldShowToDos(loanId: string, borrowerId: string): Promise<boolean> {
    try {
      const metadata = await this.loadMetadata(loanId, borrowerId);
      return metadata.showToDos;
    } catch {
      return false;
    }
  }

  async getUnreadMessageCount(loanId: string, borrowerId: string): Promise<number> {
    const metadata = await this.loadMetadata(loanId, borrowerId);
    return metadata.unreadMessageCount;
  }
}
